import React, { createRef, useEffect, useMemo, useRef, useState } from 'react';
import T from 'i18n';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import ActionsButton from '../../common/ActionsButton';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useDispatch, useSelector } from 'react-redux';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { setSummaryOpen } from 'features/common/redux/navbarLayout';
import {
  selectPermissions,
  selectFolderPath,
  selectCustomContainers,
  selectContainers,
  selectDownloadedDocuments,
  selectGenerateHyperlinks,
  selectCurrentFolder,
  selectConfidentialityRing,
  selectCurrentUserConfidentialityRing,
  selectUserAiTools,
  selectCurrentCaseId,
  selectIsUploadDrawerOpen,
  selectIManageEnabled,
  selectUserId,
  selectGlobalSideBarOpen,
  selectGlobalSummaryOpen,
  selectUserSettingPresentMode,
  selectShowExhibitNumber,
  selectEnableRemovePages,
  selectCanUpdateExhibitReferences,
  selectCurrentFile,
  selectFolders,
  selectDocuments,
  selectUseOldFilter,
  selectUseAskAiSSEChat,
} from 'common/selectors';
import { removeIndexFolders } from 'utils/foldersStructuring';
import history from 'common/history';
import FilterChips from './FilterChips';
import useSearchContext from 'features/common/Search/useSearchContext';
import { setAiToolsFilters } from '../redux/setAiToolsFilters';
import { setAskAiSearchType, openAskAiSse } from '../redux/actions';
import { setAskAiSseSelectedFiles } from '../redux/askAiSse';
import { Spinner } from 'features/common';
import {
  WarningOutlined,
  Refresh,
  History,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import {
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Select,
  FormControl,
  Typography,
  Box,
  Autocomplete,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import FilterOption from 'features/common/Filters/FilterOption';
import DatePickers from 'features/common/Filters/DatePickers';
import FilterNameChip from './FilterNameChip';
import { SelectChangeEvent } from '@mui/material/Select';
import { useSetFilterOptions } from '../redux/setFilterOptions';
import {
  clearDocuments,
  fetchDocuments,
  fetchFolder,
  fetchTimeline,
  fetchContainer,
  fetchCaseAnnotations,
} from '../redux/actions';
import SearchContentHistory from '../SearchCenter/SearchContentHistory';
import { buttonActions } from './utils/buttonActions';
import {
  getBaseKey,
  getNextInstanceKey,
  isFilterValuesEmpty,
  getAppliedFilters,
} from './utils/filterHelpers';
import { DOC_TYPE_OPTIONS, getUpfrontFilters, getMoreFilters } from './constants/filterConstants';
import {
  getTrialBundlesOptions,
  getTeamBundlesOptions,
  getPrivateBundlesOptions,
  getClassifyTypeOptions,
  getClassifySubtypeOptions,
  getAuthorsOptions,
  getRecipientsOptions,
} from './utils/filterOptions';
import {
  shouldHideRefresh,
  shouldShowAIIcon,
  isSearchModePage,
  shouldShowAddFilterButton,
  checkPathForCustomContainers,
} from './utils/navigationHelpers';
import {
  getMergedFilesTooltip,
  getMergedSelectedFileNames,
} from './utils/aiToolsMergeHelpers';

const breadcrumbMeasuredRef = createRef<any>();

export default ({
  filterCategory,
  selectedRows,
  openDialog,
  handleShow,
  isAllDocuments,
  isPresenterTools,
  category,
  getFolderSpecificFileDetails,
  openFileLoading,
  openDialogCR,
  sortMode,
  handleSidebar,
  isDocFinderPage,
  fileCount,
  showSidebar,
  batchUploading,
  setEditMode,
  editMode,
  openMapDataDrawer,
  mappedRowsWithErrors,
  handleOpenAssignDocuments,
  showAIIcon,
  handleOpenViewDrawer,
  hideAiToolsForMergedDocuments = false,
  openCreatePlaceholdersModal,
  openIManageDrawer,
  filter,
  setFilter,
  filterOperators: externalFilterOperators,
  setFilterOperators: externalSetFilterOperators,
  filterOrder: externalFilterOrder,
  setFilterOrder: externalSetFilterOrder,
}: any) => {
  const [collapseBreadCrumb, setCollapseBreadCrumb] = useState(true);
  const [searchHistoryAnchorEl, setSearchHistoryAnchorEl] = useState<null | HTMLElement>(null);
  const { setSearchValue } = useSearchContext() as any;
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFilter, setSelectedFilter] = useState<string>('');
  const [tempFilterValues, setTempFilterValues] = useState<any>({});
  const [isEditingFilter, setIsEditingFilter] = useState<boolean>(false);
  const [internalFilterOrder, setInternalFilterOrder] = useState<string[]>([]);
  const [internalFilterOperators, setInternalFilterOperators] = useState<
    Record<string, 'and' | 'or'>
  >({});
  const [tempOperator, setTempOperator] = useState<'and' | 'or'>('and');
  const [showMoreFilters, setShowMoreFilters] = useState<boolean>(false);
  const [selectOpen, setSelectOpen] = useState<boolean>(false);
  const filterMenuContentRef = useRef<HTMLDivElement | null>(null);
  const filterMenuPaperRef = useRef<HTMLDivElement | null>(null);
  const isApplyingFilterRef = useRef(false);
  const pageCountInputRef = useRef<HTMLInputElement | null>(null);
  // Prevent the Select from closing immediately after toggling more/less filters
  const preventNextCloseRef = useRef(false);

  // Use external state if provided, otherwise use internal state
  const filterOrder = externalFilterOrder !== undefined ? externalFilterOrder : internalFilterOrder;
  const setFilterOrder = externalSetFilterOrder || setInternalFilterOrder;
  const filterOperators =
    externalFilterOperators !== undefined ? externalFilterOperators : internalFilterOperators;
  const setFilterOperators = externalSetFilterOperators || setInternalFilterOperators;

  const openFilterMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    setFilterMenuAnchorEl(e.currentTarget);
    setTempFilterValues({});
    setSelectedFilter('');
    setIsEditingFilter(false);
    setTempOperator('and'); // Default to 'and' for new filters
  };

  const closeFilterMenu = () => {
    setFilterMenuAnchorEl(null);
    setTempFilterValues({});
    setSelectedFilter('');
    setIsEditingFilter(false);
    setShowMoreFilters(false); // Reset more filters state when closing menu
  };

  // Enhanced close handler: if user clicks outside (backdrop) and a filter with non-empty values
  // is currently selected (i.e., Apply would act), automatically apply the filter instead of just closing.
  const handleFilterMenuClose = (event: any, reason: any) => {
    // Only auto-apply on backdrop clicks (outside menu). For escape/tab, keep existing behavior.
    if (
      reason === 'backdropClick' &&
      selectedFilter &&
      // Use the same emptiness logic as apply to avoid applying empty filters
      !isFilterValuesEmpty(
        isEditingFilter ? getBaseKey(selectedFilter) : selectedFilter,
        tempFilterValues,
      ) &&
      !isApplyingFilterRef.current
    ) {
      handleApplyFilter();
      return;
    }
    closeFilterMenu();
  };

  const handleApplyFilter = () => {
    // Prevent re-entry
    if (isApplyingFilterRef.current) {
      return;
    }

    // For editing, get the base key from selectedFilter (which might be an instance key)
    const baseKeyToCheck = isEditingFilter ? getBaseKey(selectedFilter) : selectedFilter;

    // Check if filter values are empty
    const isEmpty = !selectedFilter || isFilterValuesEmpty(baseKeyToCheck, tempFilterValues);

    if (isEmpty) {
      // If we're editing an existing filter and all values are removed, remove the filter
      if (isEditingFilter && selectedFilter && filterOrder.includes(selectedFilter)) {
        handleRemoveFilter(selectedFilter);
      }
      closeFilterMenu();
      return;
    }

    // Set the flag to prevent re-entry
    isApplyingFilterRef.current = true;

    // Capture values before any state changes
    const baseKey = isEditingFilter ? getBaseKey(selectedFilter) : selectedFilter;
    const instanceKey = isEditingFilter ? selectedFilter : getNextInstanceKey(baseKey, filterOrder);
    const filterValuesToApply = { ...tempFilterValues };
    const operatorToApply = tempOperator;
    const isNewFilter = !filterOrder.includes(instanceKey);
    const isFirstFilter = filterOrder.length === 0;

    // Update all state BEFORE closing menu
    // 1. Update filter with new values using instance key
    setFilter({ ...filter, [instanceKey]: filterValuesToApply });

    // 2. Update filter order if this is a new filter
    if (isNewFilter) {
      setFilterOrder([...filterOrder, instanceKey]);
    }

    // 3. Update operators if needed (skip for the very first filter)
    if (isNewFilter && !isFirstFilter) {
      // Adding a new filter (not the first one)
      setFilterOperators({
        ...filterOperators,
        [instanceKey]: operatorToApply,
      });
    } else if (!isNewFilter) {
      // Editing existing filter - update operator if changed
      const currentOp = filterOperators[instanceKey];
      if (currentOp !== operatorToApply) {
        setFilterOperators({
          ...filterOperators,
          [instanceKey]: operatorToApply,
        });
      }
    }

    // 4. Close menu LAST
    closeFilterMenu();

    // Reset the flag after a short delay to allow state updates to complete
    setTimeout(() => {
      isApplyingFilterRef.current = false;
    }, 100);
  };

  const handleCancelFilter = () => {
    closeFilterMenu();
  };

  const handleFilterChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value as string;
    // Special sentinel value to toggle more/less filters without selecting an actual filter
    if (value === '__toggle_more_filters') {
      setShowMoreFilters((prev) => !prev);
      // Keep menu open after toggling
      setSelectOpen(true);
      return;
    }
    setSelectedFilter(value);
    setTempFilterValues({});
  };

  const handleEditFilter = (instanceKey: string, e: React.MouseEvent<HTMLElement>) => {
    setSelectedFilter(instanceKey);
    setIsEditingFilter(true);

    const baseKey = getBaseKey(instanceKey);

    // Get filter values from the instance-specific location
    const filterInstance = filter[instanceKey] || {};

    // Copy current filter values to temp
    const currentValues: any = {};

    // Handle different filter types
    switch (baseKey) {
      case 'date':
        currentValues.dateFrom = filterInstance.dateFrom ? new Date(filterInstance.dateFrom) : null;
        currentValues.dateTo = filterInstance.dateTo ? new Date(filterInstance.dateTo) : null;
        currentValues.dateFilter = filterInstance.dateFilter;
        break;

      case 'dateUploaded':
        currentValues.dateUploadedFrom = filterInstance.dateUploadedFrom
          ? new Date(filterInstance.dateUploadedFrom)
          : null;
        currentValues.dateUploadedTo = filterInstance.dateUploadedTo
          ? new Date(filterInstance.dateUploadedTo)
          : null;
        currentValues.dateUploadedFilter = filterInstance.dateUploadedFilter;
        break;

      case 'annotations':
        currentValues.withAnnotations = filterInstance.withAnnotations;
        currentValues.withoutAnnotations = filterInstance.withoutAnnotations;
        break;

      case 'hyperlinks':
        currentValues.withPublicHyperlinks = filterInstance.withPublicHyperlinks;
        currentValues.withoutPublicHyperlinks = filterInstance.withoutPublicHyperlinks;
        break;

      case 'visibility':
        currentValues.private = filterInstance.private;
        currentValues.public = filterInstance.public;
        break;

      case 'flag':
        currentValues.withFlag = filterInstance.withFlag;
        currentValues.noFlag = filterInstance.noFlag;
        break;

      case 'merged':
        currentValues.withMerged = filterInstance.withMerged;
        currentValues.notMerged = filterInstance.notMerged;
        break;

      case 'pageCount':
        currentValues.pageCountOperator = filterInstance.pageCountOperator || '=';
        currentValues.pageCountValue = filterInstance.pageCountValue || '';
        break;

      case 'authors':
      case 'recipients':
      case 'trialBundles':
      case 'teamBundles':
      case 'privateBundles':
      case 'classifyType':
      case 'classifySubtype':
      case 'docType':
        // For array-based filters, check if nested or direct
        currentValues[baseKey] =
          filterInstance[baseKey] ||
          (Array.isArray(filterInstance) || typeof filterInstance === 'object'
            ? filterInstance
            : []);
        break;

      default:
        // Generic fallback for other filter types
        currentValues[baseKey] = filterInstance[baseKey] || filterInstance;
        break;
    }

    setTempFilterValues(currentValues);

    // Set the current operator for this filter (if it exists)
    setTempOperator(filterOperators[instanceKey] || 'and');

    setFilterMenuAnchorEl(e.currentTarget);
  };

  const handleRemoveFilter = (instanceKey: string) => {
    const newFilter = { ...filter };

    // Remove the instance-specific filter data
    delete newFilter[instanceKey];

    setFilter(newFilter);

    // Remove from filter order
    setFilterOrder(filterOrder.filter((key: string) => key !== instanceKey));

    // Remove operator for this filter
    const newOperators = { ...filterOperators };
    delete newOperators[instanceKey];
    setFilterOperators(newOperators);
  };

  useEffect(() => {
    if (filterMenuAnchorEl) {
      if (filterMenuPaperRef.current) {
        filterMenuPaperRef.current.focus();
      } else if (filterMenuContentRef.current) {
        filterMenuContentRef.current.focus();
      }
    }
  }, [filterMenuAnchorEl]);

  const handleFilterMenuEntered = () => {
    requestAnimationFrame(() => {
      const paperEl = filterMenuPaperRef.current;
      if (!paperEl) return;
      const firstFocusable = paperEl.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ) as HTMLElement | null;
      (firstFocusable || paperEl).focus();
    });
  };
  // Upfront filters that are always visible
  const upfrontFilters = useMemo(() => getUpfrontFilters(), []);

  // Additional filters shown when "More Filters" is expanded
  const moreFilters = useMemo(() => getMoreFilters(), []);

  // Combined list of all filters (kept for reference purposes)
  const allFilterOptions = useMemo(
    () => [...upfrontFilters, ...moreFilters],
    [upfrontFilters, moreFilters],
  );

  // All filter options are always available (duplicates allowed)
  const availableFilterOptions = useMemo(() => {
    return showMoreFilters ? allFilterOptions : upfrontFilters;
  }, [showMoreFilters, allFilterOptions, upfrontFilters]);

  const path = useSelector((state) => selectFolderPath(state, category));
  const isCustomContainers = useSelector(selectCustomContainers);
  const containers = isCustomContainers && useSelector(selectContainers);
  const permissions = useSelector(selectPermissions);
  const { clearSearch } = useSearchContext();
  const downloadedDocuments = useSelector(selectDownloadedDocuments);
  const showGenerateHyperlinks = useSelector(selectGenerateHyperlinks);
  const showExhibitNumber = useSelector(selectShowExhibitNumber);
  const enableRemovePages = useSelector(selectEnableRemovePages);
  const canUpdateExhibitReferences = useSelector(selectCanUpdateExhibitReferences);
  const updateReferencesDisabled = false;
  const currentFolder = useSelector(selectCurrentFolder);
  const currrentUserCR = useSelector(selectCurrentUserConfidentialityRing);
  const globalBatchDrawerOpen = useSelector(selectIsUploadDrawerOpen);
  const isSummaryOpen = useSelector(selectGlobalSummaryOpen);
  const isFileOpen = useSelector(selectCurrentFile);
  const filterChipMarginTop = isFileOpen ? 0 : 4;
  const {
    documents: { inlineEdit },
  } = permissions;
  const canPresentToHearingRoom = permissions?.hearingRoom?.canPresent?.('public');
  const showPresentModeWithSearch = useSelector(selectUserSettingPresentMode) && canPresentToHearingRoom;
  const isConfidentialityRing =
    useSelector(selectConfidentialityRing) && currrentUserCR !== 'noRing';
  const showAiTools = useSelector(selectUserAiTools);
  const caseId = useSelector(selectCurrentCaseId);
  const isSidebarOpen = useSelector(selectGlobalSideBarOpen);
  const showIManageTools = useSelector(selectIManageEnabled);
  const userId = useSelector(selectUserId);
  const useOldFilter = useSelector(selectUseOldFilter);
  const folders = useSelector(selectFolders);
  const documents = useSelector(selectDocuments);
  const useAskAiSSEChat = useSelector(selectUseAskAiSSEChat);
  const dispatch = useDispatch();
  const { filterOptions } = useSetFilterOptions();

  const handleAiToolsClicked = (files: any, type?: string) => {
    dispatch(openAskAiSse());
    dispatch(setSummaryOpen(true));
    if (type === 'files') {
      dispatch(setAskAiSearchType('multiFiles'));
    } else if (type === 'folders') {
      dispatch(setAskAiSearchType('bundle'));
    } else if (type === 'compareFiles') {
      dispatch(setAskAiSearchType('compareFiles'));
    }

    if (type === 'files' || type === 'compareFiles') {
      const mappedFiles = Array.isArray(files)
        ? files.map((file: any) => ({ id: file.id, name: file.name }))
        : [];
      if (useAskAiSSEChat) {
        dispatch(setAskAiSseSelectedFiles({ files: mappedFiles, selectionType: type }));
      } else {
        dispatch(setAiToolsFilters({ files: mappedFiles }));
      }
    } else if (type === 'folders') {
      dispatch(setAiToolsFilters({ folder: { id: currentFolder.id, name: currentFolder.name } }));
    }
    // Add askAI=true to the current URL
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('askAI', 'true');
    history.push(currentUrl.pathname + currentUrl.search);
  };

  const trialBundlesOptions = useMemo(
    () => getTrialBundlesOptions(folders, removeIndexFolders),
    [folders],
  );

  const teamBundlesOptions = useMemo(() => getTeamBundlesOptions(folders), [folders]);

  const privateBundlesOptions = useMemo(() => getPrivateBundlesOptions(folders), [folders]);

  const classifyTypeOptions = useMemo(
    () => getClassifyTypeOptions(filterOptions, documents),
    [filterOptions, documents],
  );

  const classifySubtypeOptions = useMemo(
    () => getClassifySubtypeOptions(filterOptions, documents),
    [filterOptions, documents],
  );

  const authorsOptions = useMemo(
    () => getAuthorsOptions(filterOptions, documents),
    [filterOptions, documents],
  );

  const recipientsOptions = useMemo(
    () => getRecipientsOptions(filterOptions, documents),
    [filterOptions, documents],
  );

  const docTypeOptions = DOC_TYPE_OPTIONS;
  const mergedSelectedFileNames = useMemo(
    () => getMergedSelectedFileNames(selectedRows),
    [selectedRows],
  );
  const hasMergedSelectedFiles = mergedSelectedFileNames.length > 0;
  const mergedFilesTooltip = useMemo(
    () => getMergedFilesTooltip(mergedSelectedFileNames),
    [mergedSelectedFileNames],
  );
  const isCompareFilesSelected = selectedRows?.length === 2;
  const isMultiFileSelection = selectedRows?.length > 1;
  const compareFilesDisabled = !isCompareFilesSelected || hasMergedSelectedFiles;
  const askAboutFilesDisabled = !isMultiFileSelection || hasMergedSelectedFiles;
  const compareFilesTooltip = hasMergedSelectedFiles
    ? mergedFilesTooltip
    : isCompareFilesSelected
      ? T.translate('case.useAIToCompareFiles')
      : T.translate('case.aiToCompareFiles');
  const askAboutFilesTooltip = hasMergedSelectedFiles
    ? mergedFilesTooltip
    : T.translate('case.useAIForFilesDisabled');

  const handleSearchHistoryClose = () => setSearchHistoryAnchorEl(null);
  const handleSearchItemClick = (item: any) => {
    // Prefer explicit query field, else fall back to name/title
    const query = item?.query || item?.searchQuery || item?.phrase || item?.name || '';
    if (setSearchValue && typeof query === 'string') {
      setSearchValue(query);
    }
    setSearchHistoryAnchorEl(null);
  };

  const handleRefresh = () => {
    dispatch(clearDocuments());

    switch (currentFolder?.type) {
      case 'quicklinks':
        dispatch(fetchDocuments({} as any));
        break;

      case 'timelines':
        dispatch(fetchTimeline({ timelineId: currentFolder.id }));
        break;

      case 'tags':
        dispatch(fetchCaseAnnotations({ tagId: currentFolder.id }));
        break;

      case 'all-annotations':
        dispatch(fetchCaseAnnotations({ isAllAnnotations: true }));
        break;

      default:
        if (currentFolder?.id) {
          dispatch(fetchFolder({ folderId: currentFolder.id }));
          dispatch(fetchDocuments({} as any));
        } else {
          dispatch(fetchDocuments({} as any));
        }
        break;
    }

    if (containers) {
      dispatch(fetchContainer({}));
    }
  };

  const handleBreadCrumbCollapse = (collapse: boolean) => {
    if (path.length > 2) {
      setCollapseBreadCrumb(collapse);
    } else {
      setCollapseBreadCrumb(collapse);
    }
  };

  const showBreadCrumb = () => {
    if (isSearchModePage()) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<History fontSize="small" />}
            onClick={(e) => setSearchHistoryAnchorEl(e.currentTarget)}
          >
            {T.translate('case.searchHistory')}
          </Button>
        </div>
      );
    }

    return batchUploading ? (
      <>
        <Breadcrumb ref={breadcrumbMeasuredRef} className="folder-breadcrumb-text">
          {path.length > 0 && <Breadcrumb.Item active>{`${path[0]}`}</Breadcrumb.Item>}
          <Breadcrumb.Item active>{` ${T.translate('generic.loading')}`}</Breadcrumb.Item>
        </Breadcrumb>
        <Spinner style={{ height: '1.5rem' }} />
      </>
    ) : (
      <div style={{ cursor: 'pointer', flexDirection: 'row' }}>
        <Breadcrumb ref={breadcrumbMeasuredRef} className="folder-breadcrumb-text">
          {collapseBreadCrumb ? (
            <Breadcrumb ref={breadcrumbMeasuredRef} onClick={() => handleBreadCrumbCollapse(false)}>
              <Breadcrumb.Item active={true}>
                {checkPathForCustomContainers(path[0], containers)}
              </Breadcrumb.Item>
              {path.length > 2 && (
                <Breadcrumb.Item href="" active={true}>
                  ...
                </Breadcrumb.Item>
              )}
              {path.length > 1 && <Breadcrumb.Item active>{path[path.length - 1]}</Breadcrumb.Item>}
            </Breadcrumb>
          ) : (
            <>
              <Breadcrumb.Item onClick={() => handleBreadCrumbCollapse(true)} active={true}>
                {checkPathForCustomContainers(path, containers).join(' / ')}
              </Breadcrumb.Item>
            </>
          )}
        </Breadcrumb>
      </div>
    );
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: '1rem',
        alignItems: 'center',
        width: '100%',
        marginRight: '0.8rem',
      }}
    >
      {/* Left section - Breadcrumb and Filter Chips */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0, // Allow shrinking
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          {showBreadCrumb()}
          {mappedRowsWithErrors.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                marginLeft: '10px',
              }}
            >
              <WarningOutlined sx={{ color: '#ECD474' }} />
              <span style={{ marginLeft: '4px' }}>
                - Data Mapping Error - File not found in CSV
              </span>
            </div>
          )}
        </div>
        {useOldFilter && (
          <PerfectScrollbar
            style={{
              maxWidth: '100%',
              flexGrow: 1,
              marginTop: filterChipMarginTop,
            }}
            className="filterChipSection"
            options={{ suppressScrollY: true, useBothWheelAxes: true }}
          >
            <FilterChips filterCategory={filterCategory} clearFilter={clearSearch} />
          </PerfectScrollbar>
        )}
        {!useOldFilter && (
          <PerfectScrollbar
            style={{
              maxWidth: '100%',
              flexGrow: 1,
              marginTop: filterChipMarginTop,
            }}
            className="filterChipSection"
            options={{ suppressScrollY: true, useBothWheelAxes: true }}
          >
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
              {getAppliedFilters(filterOrder, filter, filterOperators).map(
                (appliedFilter: { key: string; label: string }) => (
                  <FilterNameChip
                    key={appliedFilter.key}
                    idx={`applied-filter-${appliedFilter.key}`}
                    label={appliedFilter.label}
                    deleteHandler={() => handleRemoveFilter(appliedFilter.key)}
                    clickHandler={(e: React.MouseEvent<HTMLElement>) =>
                      handleEditFilter(appliedFilter.key, e)
                    }
                  />
                ),
              )}
              {shouldShowAddFilterButton() && !useOldFilter && (
                <Button
                  variant="text"
                  size="small"
                  sx={{
                    padding: '.25rem .5rem',
                    textTransform: 'none',
                    fontSize: '12px',
                    '& .MuiButton-startIcon': { marginRight: '4px' },
                    '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 14 },
                  }}
                  onClick={openFilterMenu}
                  startIcon={<FilterAltIcon sx={{ color: '#4567e5', opacity: 0.85 }} />}
                >
                  {T.translate('generic.addFilter')}
                </Button>
              )}
              <Menu
                anchorEl={filterMenuAnchorEl}
                open={Boolean(filterMenuAnchorEl)}
                onClose={handleFilterMenuClose}
                TransitionProps={{ onEntered: handleFilterMenuEntered }}
                MenuListProps={{
                  autoFocus: true,
                  onKeyDown: (e: React.KeyboardEvent) => {
                    // Prevent Tab from closing the menu, but allow Escape
                    if (e.key === 'Tab') {
                      e.stopPropagation();
                    }
                  },
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                  paper: {
                    ref: filterMenuPaperRef,
                    elevation: 8,
                    sx: {
                      zIndex: 9999,
                      boxShadow: (theme) => theme.shadows[8],
                    },
                    tabIndex: -1,
                  },
                }}
              >
                <Box
                  ref={filterMenuContentRef}
                  tabIndex={-1}
                  sx={{ p: 1.5, minWidth: 450, width: 'auto', maxWidth: 750 }}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    // Prevent Tab from closing the menu
                    if (e.key === 'Tab') {
                      e.stopPropagation();
                    }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    {isEditingFilter
                      ? `${T.translate('generic.edit')} ${allFilterOptions.find((opt) => opt.key === getBaseKey(selectedFilter))?.label || T.translate('generic.filter')}`
                      : T.translate('generic.addFilter')}
                  </Typography>

                  {/* AND/OR Operator Selection - shown for 2nd filter onwards */}
                  {filterOrder.length > 0 &&
                    (!isEditingFilter || filterOrder.indexOf(selectedFilter) > 0) && (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'auto 1fr',
                          alignItems: 'center',
                          columnGap: '16px',
                          marginBottom: '10px',
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          {T.translate('filters.operator')}
                        </Typography>
                        <RadioGroup
                          row
                          value={tempOperator}
                          onChange={(e) => setTempOperator(e.target.value as 'and' | 'or')}
                          sx={{
                            columnGap: '8px',
                            '& .MuiFormControlLabel-root': { pl: '6px', mr: '6px', mb: '0px' },
                          }}
                        >
                          <FormControlLabel
                            value="and"
                            control={<Radio size="small" sx={{ p: '4px' }} />}
                            label={T.translate('generic.AND')}
                            sx={{
                              mr: '6px',
                              '& .MuiFormControlLabel-label': { ml: '1px' },
                            }}
                          />
                          <FormControlLabel
                            value="or"
                            control={<Radio size="small" sx={{ p: '4px' }} />}
                            label={T.translate('generic.OR')}
                            sx={{
                              mr: 0,
                              '& .MuiFormControlLabel-label': { ml: '1px' },
                            }}
                          />
                        </RadioGroup>
                      </div>
                    )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                    {!isEditingFilter && (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'auto 1fr',
                          alignItems: 'center',
                          columnGap: '8px',
                        }}
                      >
                        <Typography
                          id="add-filter-filter-label"
                          variant="body2"
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {T.translate('generic.filter')}
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            aria-labelledby="add-filter-filter-label"
                            value={selectedFilter}
                            onChange={handleFilterChange}
                            open={selectOpen}
                            onOpen={() => setSelectOpen(true)}
                            onClose={() => {
                              // If we just toggled more/less filters, ignore this close
                              if (preventNextCloseRef.current) {
                                preventNextCloseRef.current = false;
                                // Force it back open on next frame
                                requestAnimationFrame(() => setSelectOpen(true));
                                return;
                              }
                              setSelectOpen(false);
                            }}
                            displayEmpty
                            renderValue={(value) => {
                              if (!value) return T.translate('generic.selectFilter');
                              const option = availableFilterOptions.find((o) => o.key === value);
                              return option ? option.label : '';
                            }}
                            MenuProps={{
                              autoFocus: false,
                              disableAutoFocusItem: true,
                              PaperProps: {
                                sx: {
                                  border: '1px solid rgba(0, 0, 0, 0.12)',
                                  maxHeight: '450px',
                                  width: 'auto',
                                  maxWidth: '300px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  '& .MuiList-root': {
                                    flex: '1 1 auto',
                                    overflowY: 'auto',
                                    maxHeight: '400px',
                                    paddingBottom: 0,
                                    // Thin scrollbar
                                    '&::-webkit-scrollbar': {
                                      width: '6px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                      background: '#f1f1f1',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                      background: '#888',
                                      borderRadius: '3px',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                      background: '#555',
                                    },
                                  },
                                  '& .filter-toggle-button': {
                                    flex: '0 0 auto',
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem disabled value="">
                              {T.translate('generic.selectFilter')}
                            </MenuItem>
                            {availableFilterOptions.map((opt) => (
                              <MenuItem key={opt.key} value={opt.key}>
                                {opt.label}
                              </MenuItem>
                            ))}
                            {/* More Filters Toggle (non-selectable to prevent closing) */}
                            <Box
                              className="filter-toggle-button"
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                preventNextCloseRef.current = true; // signal to keep open
                                setShowMoreFilters((prev) => !prev);
                                setSelectOpen(true);
                              }}
                              onMouseDown={(e) => {
                                // Prevent default mousedown that would trigger menu item selection close
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  preventNextCloseRef.current = true;
                                  setShowMoreFilters((prev) => !prev);
                                  setSelectOpen(true);
                                }
                              }}
                              sx={{
                                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                                py: 1,
                                px: 2,
                                backgroundColor: '#fff',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                                '&:focus': {
                                  outline: '2px solid #4567e5',
                                  outlineOffset: '2px',
                                },
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, color: '#4567e5', pr: 1 }}
                              >
                                {showMoreFilters
                                  ? T.translate('generic.lessFilters')
                                  : T.translate('generic.moreFilters')}
                              </Typography>
                              {showMoreFilters ? (
                                <KeyboardArrowUp sx={{ color: '#4567e5' }} />
                              ) : (
                                <KeyboardArrowDown sx={{ color: '#4567e5' }} />
                              )}
                            </Box>
                          </Select>
                        </FormControl>
                      </div>
                    )}

                    {(() => {
                      // Get the base key for filter type checking (handles both base keys and instance keys)
                      const filterTypeToCheck = selectedFilter ? getBaseKey(selectedFilter) : '';

                      return (
                        <>
                          {filterTypeToCheck === 'trialBundles' && (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                alignItems: 'center',
                                columnGap: '16px',
                              }}
                            >
                              <Typography
                                id="add-filter-value-label"
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {T.translate('generic.value')}
                              </Typography>
                              <div>
                                <FilterOption
                                  originalArray={trialBundlesOptions}
                                  filterName={T.translate('generic.value') as any}
                                  filterKey="trialBundles"
                                  valKey="id"
                                  labelKey="name"
                                  setFilters={(newFilters: any) =>
                                    setTempFilterValues({ ...tempFilterValues, ...newFilters })
                                  }
                                  filterCategory={{
                                    trialBundles: tempFilterValues.trialBundles || [],
                                  }}
                                  onChange={() => {
                                    // no-op: selected values stored in tempFilterValues
                                  }}
                                  inline
                                  placeholder={T.translate('filters.selectSearchLabel')}
                                  hideLabel
                                />
                              </div>
                            </div>
                          )}

                          {filterTypeToCheck === 'teamBundles' && (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                alignItems: 'center',
                                columnGap: '16px',
                              }}
                            >
                              <Typography
                                id="add-filter-value-label"
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {T.translate('generic.value')}
                              </Typography>
                              <div>
                                <FilterOption
                                  originalArray={teamBundlesOptions}
                                  filterName={T.translate('generic.value') as any}
                                  filterKey="teamBundles"
                                  valKey="id"
                                  labelKey="name"
                                  setFilters={(newFilters: any) =>
                                    setTempFilterValues({ ...tempFilterValues, ...newFilters })
                                  }
                                  filterCategory={{
                                    teamBundles: tempFilterValues.teamBundles || [],
                                  }}
                                  onChange={() => {
                                    // no-op
                                  }}
                                  inline
                                  placeholder={T.translate('filters.selectSearchLabel')}
                                  hideLabel
                                />
                              </div>
                            </div>
                          )}

                          {filterTypeToCheck === 'privateBundles' && (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                alignItems: 'center',
                                columnGap: '16px',
                              }}
                            >
                              <Typography
                                id="add-filter-value-label"
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {T.translate('generic.value')}
                              </Typography>
                              <div>
                                <FilterOption
                                  originalArray={privateBundlesOptions}
                                  filterName={T.translate('generic.value') as any}
                                  filterKey="privateBundles"
                                  valKey="id"
                                  labelKey="name"
                                  setFilters={(newFilters: any) =>
                                    setTempFilterValues({ ...tempFilterValues, ...newFilters })
                                  }
                                  filterCategory={{
                                    privateBundles: tempFilterValues.privateBundles || [],
                                  }}
                                  onChange={() => {
                                    // no-op
                                  }}
                                  inline
                                  placeholder={T.translate('filters.selectSearchLabel')}
                                  hideLabel
                                />
                              </div>
                            </div>
                          )}

                          {filterTypeToCheck === 'date' && (
                            <div style={{ width: '100%' }}>
                              <DatePickers
                                filterCategory={tempFilterValues}
                                label={T.translate('generic.value')}
                                setFilters={(newFilters: any) =>
                                  setTempFilterValues({ ...tempFilterValues, ...newFilters })
                                }
                              />
                            </div>
                          )}

                          {filterTypeToCheck === 'dateUploaded' && (
                            <div style={{ width: '100%' }}>
                              <DatePickers
                                filterCategory={tempFilterValues}
                                setFilters={(newFilters: any) =>
                                  setTempFilterValues({ ...tempFilterValues, ...newFilters })
                                }
                                label={T.translate('generic.value')}
                                fieldPrefix="dateUploaded"
                              />
                            </div>
                          )}

                          {filterTypeToCheck === 'classifyType' && (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                alignItems: 'center',
                                columnGap: '16px',
                              }}
                            >
                              <Typography
                                id="add-filter-value-label"
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {T.translate('generic.value')}
                              </Typography>
                              <div>
                                <FilterOption
                                  originalArray={classifyTypeOptions}
                                  filterName={T.translate('generic.value') as any}
                                  filterKey="classifyType"
                                  setFilters={(newFilters: any) =>
                                    setTempFilterValues({ ...tempFilterValues, ...newFilters })
                                  }
                                  filterCategory={{
                                    classifyType: tempFilterValues.classifyType || [],
                                  }}
                                  onChange={() => {
                                    // no-op
                                  }}
                                  inline
                                  placeholder={T.translate('filters.selectSearchLabel')}
                                  hideLabel
                                />
                              </div>
                            </div>
                          )}

                          {filterTypeToCheck === 'authors' && (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                alignItems: 'center',
                                columnGap: '16px',
                              }}
                            >
                              <Typography
                                id="add-filter-value-label"
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {T.translate('generic.value')}
                              </Typography>
                              <div>
                                <FilterOption
                                  originalArray={authorsOptions}
                                  filterName={T.translate('generic.value') as any}
                                  filterKey="authors"
                                  setFilters={(newFilters: any) =>
                                    setTempFilterValues({ ...tempFilterValues, ...newFilters })
                                  }
                                  filterCategory={{ authors: tempFilterValues.authors || [] }}
                                  onChange={() => {
                                    // no-op
                                  }}
                                  inline
                                  placeholder={T.translate('filters.selectSearchLabel')}
                                  hideLabel
                                />
                              </div>
                            </div>
                          )}

                          {filterTypeToCheck === 'recipients' && (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                alignItems: 'center',
                                columnGap: '16px',
                              }}
                            >
                              <Typography
                                id="add-filter-value-label"
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {T.translate('generic.value')}
                              </Typography>
                              <div>
                                <FilterOption
                                  originalArray={recipientsOptions}
                                  filterName={T.translate('generic.value') as any}
                                  filterKey="recipients"
                                  setFilters={(newFilters: any) =>
                                    setTempFilterValues({ ...tempFilterValues, ...newFilters })
                                  }
                                  filterCategory={{ recipients: tempFilterValues.recipients || [] }}
                                  onChange={() => {
                                    // no-op
                                  }}
                                  inline
                                  placeholder={T.translate('filters.selectSearchLabel')}
                                  hideLabel
                                />
                              </div>
                            </div>
                          )}

                          {filterTypeToCheck === 'classifySubtype' && (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                alignItems: 'center',
                                columnGap: '16px',
                              }}
                            >
                              <Typography
                                id="add-filter-value-label"
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {T.translate('generic.value')}
                              </Typography>
                              <div>
                                <FilterOption
                                  originalArray={classifySubtypeOptions}
                                  filterName={T.translate('generic.value') as any}
                                  filterKey="classifySubtype"
                                  setFilters={(newFilters: any) =>
                                    setTempFilterValues({ ...tempFilterValues, ...newFilters })
                                  }
                                  filterCategory={{
                                    classifySubtype: tempFilterValues.classifySubtype || [],
                                  }}
                                  onChange={() => {
                                    // no-op
                                  }}
                                  inline
                                  placeholder={T.translate('filters.selectSearchLabel')}
                                  hideLabel
                                />
                              </div>
                            </div>
                          )}

                          {filterTypeToCheck === 'annotations' && (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                alignItems: 'center',
                                columnGap: '16px',
                              }}
                            >
                              <Typography
                                id="add-filter-value-label"
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {T.translate('generic.value')}
                              </Typography>
                              <Autocomplete
                                size="small"
                                clearOnEscape
                                options={[
                                  {
                                    value: 'withoutAnnotations',
                                    label: T.translate('filters.withoutAnnotations'),
                                  },
                                  {
                                    value: 'withAnnotations',
                                    label: T.translate('filters.withAnnotations'),
                                  },
                                ]}
                                getOptionLabel={(option) => option.label}
                                value={
                                  tempFilterValues?.withAnnotations
                                    ? {
                                        value: 'withAnnotations',
                                        label: T.translate('filters.withAnnotations'),
                                      }
                                    : tempFilterValues?.withoutAnnotations
                                      ? {
                                          value: 'withoutAnnotations',
                                          label: T.translate('filters.withoutAnnotations'),
                                        }
                                      : null
                                }
                                onChange={(event, newValue) => {
                                  if (newValue) {
                                    setTempFilterValues({
                                      ...tempFilterValues,
                                      withAnnotations: newValue.value === 'withAnnotations',
                                      withoutAnnotations: newValue.value === 'withoutAnnotations',
                                    });
                                  } else {
                                    setTempFilterValues({
                                      ...tempFilterValues,
                                      withAnnotations: false,
                                      withoutAnnotations: false,
                                    });
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} variant="standard" size="small" />
                                )}
                                sx={{ flex: 1 }}
                              />
                            </div>
                          )}

                          {filterTypeToCheck === 'hyperlinks' && (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                alignItems: 'center',
                                columnGap: '16px',
                              }}
                            >
                              <Typography
                                id="add-filter-value-label"
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {T.translate('generic.value')}
                              </Typography>
                              <Autocomplete
                                size="small"
                                clearOnEscape
                                options={[
                                  {
                                    value: 'withoutPublicHyperlinks',
                                    label: T.translate('filters.withoutPublicHyperlinks'),
                                  },
                                  {
                                    value: 'withPublicHyperlinks',
                                    label: T.translate('filters.withPublicHyperlinks'),
                                  },
                                ]}
                                getOptionLabel={(option) => option.label}
                                value={
                                  tempFilterValues?.withoutPublicHyperlinks
                                    ? {
                                        value: 'withoutPublicHyperlinks',
                                        label: T.translate('filters.withoutPublicHyperlinks'),
                                      }
                                    : tempFilterValues?.withPublicHyperlinks
                                      ? {
                                          value: 'withPublicHyperlinks',
                                          label: T.translate('filters.withPublicHyperlinks'),
                                        }
                                      : null
                                }
                                onChange={(event, newValue) => {
                                  if (newValue) {
                                    setTempFilterValues({
                                      ...tempFilterValues,
                                      withPublicHyperlinks:
                                        newValue.value === 'withPublicHyperlinks',
                                      withoutPublicHyperlinks:
                                        newValue.value === 'withoutPublicHyperlinks',
                                    });
                                  } else {
                                    setTempFilterValues({
                                      ...tempFilterValues,
                                      withPublicHyperlinks: false,
                                      withoutPublicHyperlinks: false,
                                    });
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} variant="standard" size="small" />
                                )}
                                sx={{ flex: 1 }}
                              />
                            </div>
                          )}

                          {filterTypeToCheck === 'visibility' && (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                alignItems: 'center',
                                columnGap: '16px',
                              }}
                            >
                              <Typography
                                id="add-filter-value-label"
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {T.translate('generic.value')}
                              </Typography>
                              <Autocomplete
                                size="small"
                                clearOnEscape
                                options={[
                                  { value: 'private', label: T.translate('filters.privateOnly') },
                                  { value: 'public', label: T.translate('filters.publicOnly') },
                                ]}
                                getOptionLabel={(option) => option.label}
                                value={
                                  tempFilterValues?.private
                                    ? {
                                        value: 'private',
                                        label: T.translate('filters.privateOnly'),
                                      }
                                    : tempFilterValues?.public
                                      ? {
                                          value: 'public',
                                          label: T.translate('filters.publicOnly'),
                                        }
                                      : null
                                }
                                onChange={(event, newValue) => {
                                  if (newValue) {
                                    setTempFilterValues({
                                      ...tempFilterValues,
                                      private: newValue.value === 'private',
                                      public: newValue.value === 'public',
                                    });
                                  } else {
                                    setTempFilterValues({
                                      ...tempFilterValues,
                                      private: false,
                                      public: false,
                                    });
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} variant="standard" size="small" />
                                )}
                                sx={{ flex: 1 }}
                              />
                            </div>
                          )}

                          {filterTypeToCheck === 'flag' && (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                alignItems: 'center',
                                columnGap: '16px',
                              }}
                            >
                              <Typography
                                id="add-filter-value-label"
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {T.translate('generic.value')}
                              </Typography>
                              <Autocomplete
                                size="small"
                                clearOnEscape
                                options={[
                                  { value: 'noFlag', label: T.translate('filters.noFlag') },
                                  { value: 'withFlag', label: T.translate('filters.withFlag') },
                                ]}
                                getOptionLabel={(option) => option.label}
                                value={
                                  tempFilterValues?.noFlag
                                    ? { value: 'noFlag', label: T.translate('filters.noFlag') }
                                    : tempFilterValues?.withFlag
                                      ? {
                                          value: 'withFlag',
                                          label: T.translate('filters.withFlag'),
                                        }
                                      : null
                                }
                                onChange={(event, newValue) => {
                                  if (newValue) {
                                    setTempFilterValues({
                                      ...tempFilterValues,
                                      withFlag: newValue.value === 'withFlag',
                                      noFlag: newValue.value === 'noFlag',
                                    });
                                  } else {
                                    setTempFilterValues({
                                      ...tempFilterValues,
                                      withFlag: false,
                                      noFlag: false,
                                    });
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} variant="standard" size="small" />
                                )}
                                sx={{ flex: 1 }}
                              />
                            </div>
                          )}

                          {filterTypeToCheck === 'merged' && (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                alignItems: 'center',
                                columnGap: '16px',
                              }}
                            >
                              <Typography
                                id="add-filter-value-label"
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {T.translate('generic.value')}
                              </Typography>
                              <Autocomplete
                                size="small"
                                clearOnEscape
                                options={[
                                  { value: 'notMerged', label: T.translate('filters.notMerged') },
                                  { value: 'withMerged', label: T.translate('filters.withMerged') },
                                ]}
                                getOptionLabel={(option) => option.label}
                                value={
                                  tempFilterValues?.notMerged
                                    ? {
                                        value: 'notMerged',
                                        label: T.translate('filters.notMerged'),
                                      }
                                    : tempFilterValues?.withMerged
                                      ? {
                                          value: 'withMerged',
                                          label: T.translate('filters.withMerged'),
                                        }
                                      : null
                                }
                                onChange={(event, newValue) => {
                                  if (newValue) {
                                    setTempFilterValues({
                                      ...tempFilterValues,
                                      withMerged: newValue.value === 'withMerged',
                                      notMerged: newValue.value === 'notMerged',
                                    });
                                  } else {
                                    setTempFilterValues({
                                      ...tempFilterValues,
                                      withMerged: false,
                                      notMerged: false,
                                    });
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} variant="standard" size="small" />
                                )}
                                sx={{ flex: 1 }}
                              />
                            </div>
                          )}

                          {filterTypeToCheck === 'docType' && (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                alignItems: 'center',
                                columnGap: '16px',
                              }}
                            >
                              <Typography
                                id="add-filter-value-label"
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {T.translate('generic.value')}
                              </Typography>
                              <div>
                                <FilterOption
                                  originalArray={docTypeOptions}
                                  filterName={T.translate('generic.value') as any}
                                  filterKey="docType"
                                  setFilters={(newFilters: any) =>
                                    setTempFilterValues({ ...tempFilterValues, ...newFilters })
                                  }
                                  filterCategory={{ docType: tempFilterValues.docType || [] }}
                                  onChange={() => {
                                    // no-op
                                  }}
                                  inline
                                  placeholder={T.translate('filters.selectSearchLabel')}
                                  hideLabel
                                />
                              </div>
                            </div>
                          )}

                          {filterTypeToCheck === 'pageCount' && (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                alignItems: 'center',
                                columnGap: '16px',
                              }}
                            >
                              <Typography
                                id="add-filter-value-label"
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {T.translate('generic.value')}
                              </Typography>
                              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                <FormControl
                                  size="small"
                                  sx={{ minWidth: '50px', maxWidth: '50px' }}
                                >
                                  <Select
                                    value={tempFilterValues.pageCountOperator || '='}
                                    onChange={(e) =>
                                      setTempFilterValues({
                                        ...tempFilterValues,
                                        pageCountOperator: e.target.value,
                                      })
                                    }
                                    variant="standard"
                                    onKeyDown={(e: React.KeyboardEvent) => {
                                      if (e.key === 'Tab' && !e.shiftKey) {
                                        e.preventDefault();
                                        e.stopPropagation(); // Prevent the event from bubbling up
                                        if (pageCountInputRef.current) {
                                          pageCountInputRef.current.focus();
                                        }
                                      }
                                    }}
                                  >
                                    <MenuItem value="=">=</MenuItem>
                                    <MenuItem value=">">&gt;</MenuItem>
                                    <MenuItem value=">=">&gt;=</MenuItem>
                                    <MenuItem value="<">&lt;</MenuItem>
                                    <MenuItem value="<=">&lt;=</MenuItem>
                                  </Select>
                                </FormControl>
                                <TextField
                                  type="number"
                                  size="small"
                                  variant="standard"
                                  placeholder={T.translate('filters.enterPageCount')}
                                  value={tempFilterValues.pageCountValue || ''}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value, 10);
                                    if (e.target.value === '' || value >= 1) {
                                      setTempFilterValues({
                                        ...tempFilterValues,
                                        pageCountOperator:
                                          tempFilterValues.pageCountOperator || '=',
                                        pageCountValue: e.target.value,
                                      });
                                    }
                                  }}
                                  inputProps={{ min: 1 }}
                                  inputRef={pageCountInputRef}
                                  sx={{ width: '120px' }}
                                />
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Apply and Cancel buttons */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 1,
                      mt: 5,
                      pt: 2,
                      borderTop: '1px solid #e0e0e0',
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCancelFilter}
                      sx={{ textTransform: 'none' }}
                    >
                      {T.translate('generic.cancel')}
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleApplyFilter}
                      disabled={!selectedFilter || Object.keys(tempFilterValues).length === 0}
                      sx={{ textTransform: 'none' }}
                    >
                      {T.translate('generic.apply')}
                    </Button>
                  </Box>
                </Box>
              </Menu>
            </div>
          </PerfectScrollbar>
        )}
      </div>
      {/* Right section - Action buttons */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          justifyContent: 'flex-end',
          flexShrink: 0, // Prevent shrinking
        }}
      >
        {showAIIcon &&
          !isFileOpen &&
          !isSummaryOpen &&
          showAiTools &&
          shouldShowAIIcon() &&
          !hideAiToolsForMergedDocuments && (
          <>
            <Tooltip title={compareFilesTooltip} enterDelay={0} leaveDelay={0} arrow>
              <span>
                <IconButton
                  onClick={() => handleAiToolsClicked(selectedRows, 'compareFiles')}
                  size="small"
                  disabled={compareFilesDisabled}
                  sx={{
                    color: !compareFilesDisabled ? '#4567e5' : '#666',
                    opacity: !compareFilesDisabled ? 0.75 : 0.5,
                    marginRight: '0.3rem',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0)',
                      color: !compareFilesDisabled ? '#4567e5' : '#666',
                      opacity: !compareFilesDisabled ? 1 : 0.5,
                    },
                    '&.Mui-disabled': {
                      color: '#666',
                      opacity: 0.5,
                    },
                  }}
                >
                  <span
                    style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '3px' }}
                  >
                    <AutoAwesomeIcon />
                    <span style={{ marginLeft: '2px', fontSize: '11px' }}>
                      {isCompareFilesSelected
                        ? T.translate('case.compareFiles')
                        : T.translate('case.selectTwoFiles')}
                    </span>
                  </span>
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={askAboutFilesTooltip} enterDelay={0} leaveDelay={0} arrow>
              <span>
                <IconButton
                  onClick={() => handleAiToolsClicked(selectedRows, 'files')}
                  size="small"
                  disabled={askAboutFilesDisabled}
                  sx={{
                    color: !askAboutFilesDisabled ? '#4567e5' : '#666',
                    opacity: !askAboutFilesDisabled ? 0.75 : 0.5,
                    marginRight: '0.3rem',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0)',
                      color: !askAboutFilesDisabled ? '#4567e5' : '#666',
                      opacity: !askAboutFilesDisabled ? 1 : 0.5,
                    },
                    '&.Mui-disabled': {
                      color: '#666',
                      opacity: 0.5,
                    },
                  }}
                >
                  <span
                    style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '3px' }}
                  >
                    <AutoAwesomeIcon />
                    <span style={{ marginLeft: '2px', fontSize: '11px' }}>
                      {T.translate('case.askAboutFiles')}
                    </span>
                  </span>
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={T.translate('case.selectedFolderAITooltip')}
              enterDelay={0}
              leaveDelay={0}
              arrow
            >
              <IconButton
                onClick={() => handleAiToolsClicked(selectedRows, 'folders')}
                size="small"
                sx={{
                  color: '#4567e5',
                  opacity: 0.75,
                  marginLeft: '0.5rem',
                  marginRight: '0.3rem',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0)',
                    color: '#4567e5',
                    opacity: 1,
                  },
                }}
              >
                <span
                  style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '3px' }}
                >
                  <AutoAwesomeIcon />
                  <span style={{ marginLeft: '2px', fontSize: '11px' }}>
                    {T.translate('case.askAboutFolder')}
                  </span>
                </span>
              </IconButton>
            </Tooltip>
          </>
        )}
        {!shouldHideRefresh() && (
          <IconButton
            onClick={handleRefresh}
            size="small"
            title="Refresh"
            sx={{
              color: '#6c757d',
              marginRight: '1rem',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0)',
                color: '#5a6268',
              },
            }}
          >
            <Refresh />
          </IconButton>
        )}
        <ActionsButton
          buttonActions={buttonActions({
            selectedRows,
            permissions,
            isPresenterTools,
            downloadedDocuments,
            currentFolder,
            openDialog,
            handleShow,
            showGenerateHyperlinks,
            showExhibitNumber,
            enableRemovePages,
            canUpdateExhibitReferences,
            updateReferencesDisabled,
            isAllDocuments,
            getFolderSpecificFileDetails,
            openFileLoading,
            dispatch,
            isConfidentialityRing,
            openDialogCR,
            caseId,
            sortMode,
            handleSidebar,
            path,
            fileCount,
            showSidebar,
            setEditMode,
            editMode,
            globalBatchDrawerOpen,
            openMapDataDrawer,
            inlineEdit,
            isDocFinderPage,
            isSidebarOpen,
            handleOpenAssignDocuments,
            showPresentModeWithSearch,
            onOpenViewDrawer: handleOpenViewDrawer,
            openCreatePlaceholdersModal,
            showIManageTools,
            userId,
            openIManageDrawer,
          })}
        />
      </div>
      <SearchContentHistory
        open={Boolean(searchHistoryAnchorEl)}
        anchorEl={searchHistoryAnchorEl}
        handleSearchHistoryClose={handleSearchHistoryClose}
        handleSearchItemClick={handleSearchItemClick}
      />
    </div>
  );
};
